// Styles only to be used for the RichTextRenderer
// Style should only target 'content-based' elements, not QuillJS focused elements

import { borderRadiusSmall, borderWidthThick, borderWidthThickest, colorBorderGlobalFocus, fontFamilyDefault, fontFamilyHeader, fontFamilyCondensed, fontSizeDefault, fontSizeLarge, fontSizeSmall, fontSizeHeader1, fontSizeHeader1Small, fontSizeHeader2, fontSizeHeader2Small, fontSizeHeader3, fontSizeHeader3Small, fontSizeHeader4, fontSizeHeader4Small, fontSizeHeader5, fontSizeHeader5Small, fontSizeHeader6, fontWeightNormal, fontWeightBold, lineHeightDefault, spacing10, spacing20, spacing40, spacing50 } from '../../styles/tokens';
import { alpha } from '@mui/system';
import makeStyles from '../../makeStyles/makeStylesInternal';

/**
 * Quill Editor v1.3.7 - Snow css converted to JSS
 * https://quilljs.com/
 **/
var styles = makeStyles(function (theme) {
  return {
    root: {
      '& .ql-container': {
        boxSizing: 'border-box',
        fontFamily: fontFamilyDefault,
        fontSize: fontSizeDefault,
        height: '100%',
        margin: '0px',
        position: 'relative'
      },
      '& .ql-editor': {
        fontFamily: fontFamilyDefault,
        fontSize: fontSizeDefault,
        boxSizing: 'border-box',
        lineHeight: '1.42',
        height: '100%',
        outline: 'none',
        overflowY: 'auto',
        padding: spacing50,
        tabSize: '1.5rem',
        mozTabSize: '1.5rem',
        textAlign: 'left',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word'
      },
      '& .ql-editor > *': {
        cursor: 'auto'
      },
      '& .ql-editor p, .ql-editor ol, .ql-editor ul, .ql-editor pre, .ql-editor blockquote, .ql-editor h1, .ql-editor h2, .ql-editor h3, .ql-editor h4, .ql-editor h5, .ql-editor h6': {
        margin: '0',
        padding: '0',
        counterReset: 'list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9'
      },
      '& .ql-editor ol, .ql-editor ul': {
        marginLeft: '1.5em'
      },
      '& .ql-editor ol > li, .ql-editor ul > li': {
        listStyleType: 'none'
      },
      '& .ql-editor ul > li::before': {
        content: '\'\\2022\''
      },
      '& .ql-editor ul[data-checked=true], .ql-editor ul[data-checked=false]': {
        pointerEvents: 'none'
      },
      '& .ql-editor ul[data-checked=true] > li *, .ql-editor ul[data-checked=false] > li *': {
        pointerEvents: 'all'
      },
      '& .ql-editor ul[data-checked=true] > li::before, .ql-editor ul[data-checked=false] > li::before': {
        color: '#777',
        cursor: 'pointer',
        pointerEvents: 'all'
      },
      '& .ql-editor ul[data-checked=true] > li::before': {
        content: '\'\\2611\''
      },
      '& .ql-editor ul[data-checked=false] > li::before': {
        content: '\'\\2610\''
      },
      '& .ql-editor li::before': {
        display: 'inline-block',
        whiteSpace: 'nowrap',
        width: '1.2em',
        marginLeft: '-1.5em',
        marginRight: '0.3em',
        textAlign: 'right'
      },
      '& .ql-editor li.ql-direction-rtl::before': {
        marginLeft: '0.3em',
        marginRight: '-1.5em',
        flip: false
      },
      '& .ql-editor ol li, .ql-editor ul li': {
        marginLeft: '1.5em'
      },
      '& .ql-editor ol li.ql-direction-rtl, .ql-editor ul li.ql-direction-rtl': {
        marginRight: '1.5em',
        flip: false
      },
      '& .ql-editor ol li': {
        counterReset: 'list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9',
        counterIncrement: 'list-0'
      },
      '& .ql-editor ol li:before': {
        content: 'counter(list-0, decimal) \'. \''
      },
      '& .ql-editor ol li.ql-indent-1': {
        counterIncrement: 'list-1',
        counterReset: 'list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9'
      },
      '& .ql-editor ol li.ql-indent-1:before': {
        content: 'counter(list-1, lower-alpha) \'. \''
      },
      '& .ql-editor ol li.ql-indent-2': {
        counterIncrement: 'list-2',
        counterReset: 'list-3 list-4 list-5 list-6 list-7 list-8 list-9'
      },
      '& .ql-editor ol li.ql-indent-2:before': {
        content: 'counter(list-2, lower-roman) \'. \''
      },
      '& .ql-editor ol li.ql-indent-3': {
        counterIncrement: 'list-3',
        counterReset: 'list-4 list-5 list-6 list-7 list-8 list-9'
      },
      '& .ql-editor ol li.ql-indent-3:before': {
        content: 'counter(list-3, decimal) \'. \''
      },
      '& .ql-editor ol li.ql-indent-4': {
        counterIncrement: 'list-4',
        counterReset: 'list-5 list-6 list-7 list-8 list-9'
      },
      '& .ql-editor ol li.ql-indent-4:before': {
        content: 'counter(list-4, lower-alpha) \'. \''
      },
      '& .ql-editor ol li.ql-indent-5': {
        counterIncrement: 'list-5',
        counterReset: 'list-6 list-7 list-8 list-9'
      },
      '& .ql-editor ol li.ql-indent-5:before': {
        content: 'counter(list-5, lower-roman) \'. \''
      },
      '& .ql-editor ol li.ql-indent-6': {
        counterIncrement: 'list-6',
        counterReset: 'list-7 list-8 list-9'
      },
      '& .ql-editor ol li.ql-indent-6:before': {
        content: 'counter(list-6, decimal) \'. \''
      },
      '& .ql-editor ol li.ql-indent-7': {
        counterIncrement: 'list-7',
        counterReset: 'list-8 list-9'
      },
      '& .ql-editor ol li.ql-indent-7:before': {
        content: 'counter(list-7, lower-alpha) \'. \''
      },
      '& .ql-editor ol li.ql-indent-8': {
        counterIncrement: 'list-8',
        counterReset: 'list-9'
      },
      '& .ql-editor ol li.ql-indent-8:before': {
        content: 'counter(list-8, lower-roman) \'. \''
      },
      '& .ql-editor ol li.ql-indent-9': {
        counterIncrement: 'list-9'
      },
      '& .ql-editor ol li.ql-indent-9:before': {
        content: 'counter(list-9, decimal) \'. \''
      },
      '& .ql-editor .ql-indent-1': {
        marginLeft: '3em'
      },
      '& .ql-editor li.ql-indent-1': {
        marginLeft: '4.5em'
      },
      '& .ql-editor .ql-indent-1.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '3em'
      },
      '& .ql-editor li.ql-indent-1.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '4.5em'
      },
      '& .ql-editor .ql-indent-2': {
        marginLeft: '6em'
      },
      '& .ql-editor li.ql-indent-2': {
        marginLeft: '7.5em'
      },
      '& .ql-editor .ql-indent-2.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '6em'
      },
      '& .ql-editor li.ql-indent-2.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '7.5em'
      },
      '& .ql-editor .ql-indent-3': {
        marginLeft: '9em'
      },
      '& .ql-editor li.ql-indent-3': {
        marginLeft: '10.5em'
      },
      '& .ql-editor .ql-indent-3.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '9em'
      },
      '& .ql-editor li.ql-indent-3.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '10.5em'
      },
      '& .ql-editor .ql-indent-4': {
        marginLeft: '12em'
      },
      '& .ql-editor li.ql-indent-4': {
        marginLeft: '13.5em'
      },
      '& .ql-editor .ql-indent-4.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '12em'
      },
      '& .ql-editor li.ql-indent-4.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '13.5em'
      },
      '& .ql-editor .ql-indent-5': {
        marginLeft: '15em'
      },
      '& .ql-editor li.ql-indent-5': {
        marginLeft: '16.5em'
      },
      '& .ql-editor .ql-indent-5.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '15em'
      },
      '& .ql-editor li.ql-indent-5.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '16.5em'
      },
      '& .ql-editor .ql-indent-6': {
        marginLeft: '18em'
      },
      '& .ql-editor li.ql-indent-6': {
        marginLeft: '19.5em'
      },
      '& .ql-editor .ql-indent-6.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '18em'
      },
      '& .ql-editor li.ql-indent-6.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '19.5em'
      },
      '& .ql-editor .ql-indent-7': {
        marginLeft: '21em'
      },
      '& .ql-editor li.ql-indent-7': {
        marginLeft: '22.5em'
      },
      '& .ql-editor .ql-indent-7.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '21em'
      },
      '& .ql-editor li.ql-indent-7.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '22.5em'
      },
      '& .ql-editor .ql-indent-8': {
        marginLeft: '24em'
      },
      '& .ql-editor li.ql-indent-8': {
        marginLeft: '25.5em'
      },
      '& .ql-editor .ql-indent-8.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '24em'
      },
      '& .ql-editor li.ql-indent-8.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '25.5em'
      },
      '& .ql-editor .ql-indent-9': {
        marginLeft: '27em'
      },
      '& .ql-editor li.ql-indent-9': {
        marginLeft: '28.5em'
      },
      '& .ql-editor .ql-indent-9.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '27em'
      },
      '& .ql-editor li.ql-indent-9.ql-direction-rtl.ql-align-right': {
        marginLeft: 0,
        marginRight: '28.5em'
      },
      '& .ql-editor .ql-video': {
        display: 'block',
        maxWidth: '100%'
      },
      '& .ql-editor .ql-video.ql-align-center': {
        margin: '0 auto'
      },
      '& .ql-editor .ql-video.ql-align-right': {
        margin: '0 0 0 auto'
      },
      '& .ql-editor .ql-bg-black': {
        backgroundColor: '#000'
      },
      '& .ql-editor .ql-bg-red': {
        backgroundColor: '#e60000'
      },
      '& .ql-editor .ql-bg-orange': {
        backgroundColor: '#f90'
      },
      '& .ql-editor .ql-bg-yellow': {
        backgroundColor: '#ff0'
      },
      '& .ql-editor .ql-bg-green': {
        backgroundColor: '#008a00'
      },
      '& .ql-editor .ql-bg-blue': {
        backgroundColor: '#06c'
      },
      '& .ql-editor .ql-bg-purple': {
        backgroundColor: '#93f'
      },
      '& .ql-editor .ql-color-white': {
        color: '#fff'
      },
      '& .ql-editor .ql-color-red': {
        color: '#e60000'
      },
      '& .ql-editor .ql-color-orange': {
        color: '#f90'
      },
      '& .ql-editor .ql-color-yellow': {
        color: '#ff0'
      },
      '& .ql-editor .ql-color-green': {
        color: '#008a00'
      },
      '& .ql-editor .ql-color-blue': {
        color: '#06c'
      },
      '& .ql-editor .ql-color-purple': {
        color: '#93f'
      },
      '& .ql-editor .ql-font-serif': {
        fontFamily: 'Georgia, Times New Roman, serif'
      },
      '& .ql-editor .ql-font-monospace': {
        fontFamily: 'Monaco, Courier New, monospace'
      },
      '& .ql-editor .ql-size-small': {
        fontSize: '0.75em'
      },
      '& .ql-editor .ql-size-large': {
        fontSize: '1.5em'
      },
      '& .ql-editor .ql-size-huge': {
        fontSize: '2.5em'
      },
      '& .ql-editor .ql-direction-rtl': {
        direction: 'rtl',
        textAlign: 'left',
        flip: false
      },
      '& .ql-editor .ql-align-center': {
        textAlign: 'center'
      },
      '& .ql-editor .ql-align-justify': {
        textAlign: 'justify'
      },
      '& .ql-editor .ql-align-right': {
        textAlign: 'right',
        flip: false
      },
      '& .ql-editor.ql-blank::before': {
        color: 'rgba(0,0,0,0.6)',
        content: 'attr(data-placeholder)',
        fontStyle: 'italic',
        left: '15px',
        pointerEvents: 'none',
        position: 'absolute',
        right: '15px'
      },
      '& .ql-editor blockquote': {
        borderLeft: borderWidthThickest + " solid " + theme.palette.grey[500],
        margin: spacing20 + " 0",
        padding: spacing40,
        fontSize: fontSizeDefault,
        lineHeight: lineHeightDefault
      },
      '& .ql-snow': {
        boxSizing: 'border-box'
      },
      '& .ql-snow *': {
        boxSizing: 'border-box'
      },
      '& .ql-editor a': {
        textDecoration: 'underline',
        color: theme.palette.action.base,
        textDecorationColor: alpha(theme.palette.action.base, .4),
        transition: theme.transitions.create(['text-decoration-color', 'color'], {
          duration: theme.transitions.duration.shortest
        }),
        '&:hover': {
          color: theme.palette.action.active,
          // cta blue 800
          cursor: 'pointer',
          textDecoration: 'underline',
          textDecorationColor: theme.palette.action.active
        },
        '&:active': {
          color: theme.palette.text.primary,
          // #151618
          textDecoration: 'underline'
        },
        '&:focus': {
          outline: borderWidthThick + " solid " + colorBorderGlobalFocus,
          outlineOffset: "" + spacing10,
          textDecoration: 'underline',
          borderRadius: borderRadiusSmall
        }
      },
      '& .ql-editor code, .ql-editor pre': {
        backgroundColor: '#f0f0f0',
        borderRadius: '3px'
      },
      '& .ql-editor pre': {
        whiteSpace: 'pre-wrap',
        marginBottom: '5px',
        marginTop: '5px',
        padding: '5px 10px'
      },
      '& .ql-editor code': {
        fontSize: '85%',
        padding: '2px 4px'
      },
      '& .ql-editor pre.ql-syntax': {
        backgroundColor: '#23241f',
        color: '#f8f8f2',
        overflow: 'visible'
      },
      '& .ql-editor img': {
        maxWidth: '100%'
      },
      // Custom Header and Body format - editor styles
      '& .ql-snow .ql-editor h1': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader1,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader1Small
        }
      },
      '& .ql-snow .ql-editor h2': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader2,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader2Small
        }
      },
      '& .ql-snow .ql-editor h3': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader3,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader3Small
        }
      },
      '& .ql-snow .ql-editor h4': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader4,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader4Small
        }
      },
      '& .ql-snow .ql-editor h5': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader5,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader5Small
        }
      },
      '& .ql-snow .ql-editor h6': {
        fontSize: fontSizeHeader6,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault
      },
      '& .ql-editor .ql-header-h1': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader1,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader1Small
        }
      },
      '& .ql-editor .ql-header-h2': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader2,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader2Small
        }
      },
      '& .ql-editor .ql-header-h3': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader3,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader3Small
        }
      },
      '& .ql-editor .ql-header-h4': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader4,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader4Small
        }
      },
      '& .ql-editor .ql-header-h5': {
        fontFamily: fontFamilyHeader,
        fontSize: fontSizeHeader5,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault,
        '@media only screen and (min-width: 0px) and (max-width: 767px)': {
          fontSize: fontSizeHeader5Small
        }
      },
      '& .ql-editor .ql-header-h6': {
        fontSize: fontSizeHeader6,
        fontWeight: fontWeightBold,
        lineHeight: lineHeightDefault
      },
      '& .ql-editor .ql-header-body1': {
        fontSize: fontSizeLarge,
        fontWeight: fontWeightNormal,
        lineHeight: lineHeightDefault
      },
      '& .ql-editor .ql-header-body2': {
        fontSize: fontSizeDefault,
        fontWeight: fontWeightNormal,
        lineHeight: lineHeightDefault
      },
      '& .ql-editor .ql-header-body3': {
        fontSize: fontSizeSmall,
        fontWeight: fontWeightNormal,
        lineHeight: lineHeightDefault
      },
      '& .ql-editor .ql-header-caption': {
        fontFamily: fontFamilyCondensed,
        fontSize: fontSizeDefault,
        fontWeight: fontWeightNormal,
        lineHeight: lineHeightDefault,
        wordSpacing: '0.16rem',
        letterSpacing: '0.0625rem',
        textTransform: 'uppercase'
      }
    },
    // These classes are not used, but need to be
    // here to prevent errors from MUI mergeClasses
    rtl: {},
    translations: {}
  };
}, {
  index: 0
});
export default styles;